import type { SearchResponse } from "src/api/SearchResponse";
import type { StaticSchedulesPassengerDetails } from "../PassengerDetailsProvider";
import type { SupportedLanguageCode } from "./language";
import { tripHashFromSearchResponse } from "./location/createTripHashForCard";
import { urlConfig } from "./urlConfig";
import type { TemplatedTicketUrl } from "./adapters/ticketInfo";

export function getPath(
  originCanonical: string | undefined,
  destinationCanonical: string | undefined,
  languageCode: SupportedLanguageCode,
) {
  const languageCodeInPath = getLanguageForUrl(languageCode);

  const parts = [];
  if (languageCodeInPath) {
    parts.push(languageCodeInPath);
  }
  parts.push("map");
  if (originCanonical) {
    parts.push(encodeURIComponent(originCanonical));
    // You can only have a destinationCanonical if you have an originCanonical.
    if (destinationCanonical) {
      parts.push(encodeURIComponent(destinationCanonical));
    }
  }

  return `/${parts.join("/")}`;
}

function getLanguageForUrl(languageCode: SupportedLanguageCode) {
  return languageCode === "en" ? undefined : languageCode;
}

export function getQueryParamValue(
  queryParam: string,
  locationSearch: string | undefined,
): string | undefined {
  if (!locationSearch) {
    return undefined;
  }

  const match = locationSearch.match(new RegExp(`${queryParam}=([^&]+)`));
  return match ? match[1] : undefined;
}

export function getTicketsUrlFromTemplate(
  template: TemplatedTicketUrl,
  exitPoint: string,
  departureDate?: string, // YYYY-MM-DD,
  returnDate?: string, // YYYY-MM-DD
  passengerDetails?: StaticSchedulesPassengerDetails,
): string {
  // API 1.5 gives us specific templated parameters. Here, we fill in ones
  // that we have values for.
  function substituteParam(url: string, param: string) {
    switch (param) {
      case "oDateTime":
        return replaceParam(url, param, departureDate);
      case "iDateTime":
        return replaceParam(url, param, returnDate);
      case "checkoutExitLabel":
        return replaceParam(url, param, exitPoint);
      case "adults":
        return replaceParam(
          url,
          param,
          passengerDetails ? passengerDetails.adults : "",
        );
      case "seniors":
        return replaceParam(
          url,
          param,
          passengerDetails ? passengerDetails.seniors : "",
        );
      case "youths":
        return replaceParam(
          url,
          param,
          passengerDetails ? passengerDetails.youths : "",
        );
      case "ages":
        return replaceParam(
          url,
          param,
          passengerDetails ? passengerDetails.ages : "",
        );
      default:
        return replaceParam(url, param, "");
    }
  }

  return template.templatedParameters.reduce(
    (urlWithSubstitutes: string, param) => {
      return substituteParam(urlWithSubstitutes, param);
    },
    template.url,
  );
}

function replaceParam(url: string, param: string, value?: string) {
  return url.replace(`{${param}}`, value ? encodeURIComponent(value) : "");
}

type QueryParams = Record<string, string | number | boolean | undefined>;

export function buildUrl(path: string, host: string, queryParams: QueryParams) {
  const url = new URL(path, host);

  for (const [key, value] of Object.entries(queryParams)) {
    if (value !== undefined) {
      url.searchParams.set(key, value.toString());
    }
  }

  return url.href;
}

export function getCarHireDeeplinkRedirectUrl(
  requestId: string | undefined, // requestId will be undefined if developing locally w/o a UID cookie
  exitPoint: string,
  locationName: string,
  lat?: number,
  lng?: number,
  carSize?: "small" | "medium" | "large",
) {
  return buildUrl("/redirects/cars", urlConfig.exitHost, {
    requestId,
    label: exitPoint,
    location: locationName,
    locationLat: lat,
    locationLng: lng,
    carClass: carSize,
  });
}

export function getHomepageUrl(languageCode: SupportedLanguageCode) {
  const languageCodeInPath = getLanguageForUrl(languageCode);

  if (languageCodeInPath) {
    const urlBuilder = new URL(
      `/${languageCodeInPath}/`,
      urlConfig.homepageHost,
    );
    return urlBuilder.href;
  }

  return urlConfig.homepageHost;
}

export function getTicketsHomepageUrl(languageCode: SupportedLanguageCode) {
  const languageCodeInPath = getLanguageForUrl(languageCode);
  const languagePath = languageCodeInPath ? `/${languageCodeInPath}` : "";

  const urlBuilder = new URL(`${languagePath}/tickets`, urlConfig.homepageHost);
  return urlBuilder.href;
}

export function getHash(
  hash: string,
  tripPlannerPrefix: boolean = false,
  searchResponse?: SearchResponse,
) {
  if (hash) {
    const isTransportDeeplink = hash.startsWith("#r");
    const isTripsTransportDeeplink = isTransportDeeplink && tripPlannerPrefix;

    // Hotels deeplinks will end in /h and include the route/segment index prior.
    // We don't want to override these.
    const isHotelsDeeplink = hash.endsWith("/h") && isTransportDeeplink;

    if (isTripsTransportDeeplink && searchResponse && !isHotelsDeeplink) {
      const searchPrefix = tripHashFromSearchResponse(searchResponse);
      const tripsHash = hash.replace("#", searchPrefix + "/");
      return tripsHash;
    }

    return hash;
  }
  return tripPlannerPrefix ? "#trips" : "";
}
